import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, MenuItem, Alert
} from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import postingService from '../../services/posting.service';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ py: 2 }}>{children}</Box>}</div>
);

// ==================== ENTRIES TAB ====================
const EntriesTab: React.FC = () => {
  const { t } = useLanguage();
  const [entries, setEntries] = useState<any[]>([]);
  const [filter, setFilter] = useState('');

  useEffect(() => { loadEntries(); }, [filter]);

  const loadEntries = async () => {
    try {
      const params: any = {};
      if (filter) params.partyType = filter;
      const res = await postingService.getSubledgerEntries(params);
      setEntries(res.data);
    } catch { }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField select label={t('subledger.party_type')} value={filter}
          onChange={e => setFilter(e.target.value)} size="small" sx={{ minWidth: 200 }}>
          <MenuItem value="">{t('subledger.all')}</MenuItem>
          <MenuItem value="CUSTOMER">{t('subledger.customer')}</MenuItem>
          <MenuItem value="SUPPLIER">{t('subledger.supplier')}</MenuItem>
        </TextField>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('common.date')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('subledger.party_type')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('subledger.party')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('subledger.entry_type')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('subledger.document')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.debit')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.credit')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.running_balance')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(e => (
              <TableRow key={e.id} hover>
                <TableCell>{new Date(e.documentDate).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Chip label={e.partyType === 'CUSTOMER' ? t('subledger.customer') : t('subledger.supplier')}
                    size="small" color={e.partyType === 'CUSTOMER' ? 'primary' : 'secondary'} variant="outlined" />
                </TableCell>
                <TableCell>{e.partyName}</TableCell>
                <TableCell>
                  <Chip label={e.entryType} size="small" variant="outlined"
                    color={e.entryType === 'INVOICE' ? 'info' : 'success'} />
                </TableCell>
                <TableCell>{e.documentNumber}</TableCell>
                <TableCell align="right">{e.debitAmount > 0 ? e.debitAmount.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}</TableCell>
                <TableCell align="right">{e.creditAmount > 0 ? e.creditAmount.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {e.runningBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </TableCell>
              </TableRow>
            ))}
            {entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {t('subledger.no_entries')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ==================== BALANCES TAB ====================
const BalancesTab: React.FC = () => {
  const { t } = useLanguage();
  const [balances, setBalances] = useState<any[]>([]);
  const [filter, setFilter] = useState('');

  useEffect(() => { loadBalances(); }, [filter]);

  const loadBalances = async () => {
    try {
      const res = await postingService.getSubledgerBalances(filter || undefined);
      setBalances(res.data);
    } catch { }
  };

  const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2 });

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField select label={t('subledger.party_type')} value={filter}
          onChange={e => setFilter(e.target.value)} size="small" sx={{ minWidth: 200 }}>
          <MenuItem value="">{t('subledger.all')}</MenuItem>
          <MenuItem value="CUSTOMER">{t('subledger.customer')}</MenuItem>
          <MenuItem value="SUPPLIER">{t('subledger.supplier')}</MenuItem>
        </TextField>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('subledger.party_type')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('subledger.party')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.total_debit')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.total_credit')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.net_balance')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('subledger.transactions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {balances.map((b, i) => (
              <TableRow key={i} hover>
                <TableCell>
                  <Chip label={b.partyType === 'CUSTOMER' ? t('subledger.customer') : t('subledger.supplier')}
                    size="small" color={b.partyType === 'CUSTOMER' ? 'primary' : 'secondary'} variant="outlined" />
                </TableCell>
                <TableCell><strong>{b.partyName}</strong></TableCell>
                <TableCell align="right">{fmt(b.totalDebit)}</TableCell>
                <TableCell align="right">{fmt(b.totalCredit)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: b.balance > 0 ? 'error.main' : b.balance < 0 ? 'success.main' : undefined }}>
                  {fmt(b.balance)}
                </TableCell>
                <TableCell align="center">{b.transactionCount}</TableCell>
              </TableRow>
            ))}
            {balances.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {t('subledger.no_entries')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ==================== AGING TAB ====================
const AgingTab: React.FC = () => {
  const { t } = useLanguage();
  const [aging, setAging] = useState<any[]>([]);
  const [filter, setFilter] = useState('CUSTOMER');

  useEffect(() => { loadAging(); }, [filter]);

  const loadAging = async () => {
    try { const res = await postingService.getAgingReport(filter); setAging(res.data); } catch { }
  };

  const fmt = (n: number) => n > 0 ? n.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—';

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField select label={t('subledger.party_type')} value={filter}
          onChange={e => setFilter(e.target.value)} size="small" sx={{ minWidth: 200 }}>
          <MenuItem value="CUSTOMER">{t('subledger.customer')}</MenuItem>
          <MenuItem value="SUPPLIER">{t('subledger.supplier')}</MenuItem>
        </TextField>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('subledger.party')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.total')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.current')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.days_1_30')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.days_31_60')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.days_61_90')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('subledger.over_90')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {aging.map((a, i) => (
              <TableRow key={i} hover>
                <TableCell><strong>{a.partyName}</strong></TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(a.total)}</TableCell>
                <TableCell align="right">{fmt(a.current)}</TableCell>
                <TableCell align="right">{fmt(a.days1to30)}</TableCell>
                <TableCell align="right" sx={{ color: a.days31to60 > 0 ? 'warning.main' : undefined }}>{fmt(a.days31to60)}</TableCell>
                <TableCell align="right" sx={{ color: a.days61to90 > 0 ? 'error.light' : undefined }}>{fmt(a.days61to90)}</TableCell>
                <TableCell align="right" sx={{ color: a.over90 > 0 ? 'error.main' : undefined }}>{fmt(a.over90)}</TableCell>
              </TableRow>
            ))}
            {aging.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {t('subledger.no_aging')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ==================== MAIN SUBLEDGER PAGE ====================
const SubledgerView: React.FC = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>{t('subledger.title')}</Typography>
      <Paper sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={t('subledger.entries')} />
          <Tab label={t('subledger.balances')} />
          <Tab label={t('subledger.aging')} />
        </Tabs>
      </Paper>
      <TabPanel value={tab} index={0}><EntriesTab /></TabPanel>
      <TabPanel value={tab} index={1}><BalancesTab /></TabPanel>
      <TabPanel value={tab} index={2}><AgingTab /></TabPanel>
    </Box>
  );
};

export default SubledgerView;
